import { useTheme } from '../ThemeProvider';
import { btn, chip, divider, kicker, progressDot, t } from '../styles';
import type { ScreenProps } from './types';

export function Onboarding({ bundle, state, dispatch }: ScreenProps) {
  const theme = useTheme();
  const ob = bundle.onboarding;
  const step = ob.steps[state.obStep] ?? ob.steps[0];
  const last = state.obStep === ob.steps.length - 1;

  return (
    <div
      style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        background: 'var(--color-bg)', zIndex: 40,
      }}
    >
      <Arcs />

      <div style={{ padding: '62px 26px 0', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--color-accent)' }} />
          <div style={kicker(10, t(0.6), 0.22)}>
            {theme.brand.name} · {bundle.event.dateRange}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 22 }}>
          {ob.steps.map((_, i) => (
            <div key={i} style={progressDot(i <= state.obStep)} />
          ))}
        </div>
      </div>

      <div className="scroll" style={{ flex: 1, overflow: 'auto', padding: '22px 26px 0', position: 'relative' }}>
        <h2
          style={{
            fontSize: 29, lineHeight: 1.1, letterSpacing: '-0.025em', margin: '0 0 10px',
            maxWidth: 300, textWrap: 'pretty', fontWeight: 500,
          }}
        >
          {step.title}
        </h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.6, color: t(0.58), margin: '0 0 24px', maxWidth: 300 }}>
          {step.sub}
        </p>

        {state.obStep === 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            {ob.pitch.map((p) => (
              <div
                key={p.t}
                style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 14px',
                  borderRadius: 8, background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
                }}
              >
                <div
                  style={{
                    width: 2, alignSelf: 'stretch', borderRadius: 2,
                    background: 'var(--color-accent)', flex: 'none',
                  }}
                />
                <div>
                  <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{p.t}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: t(0.55), marginTop: 3 }}>{p.s}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {state.obStep === 1 && (
          <div style={{ display: 'grid', gap: 20 }}>
            {ob.interestGroups.map((g) => (
              <div key={g.label}>
                <div style={{ ...kicker(10, t(0.42), 0.16), marginBottom: 10 }}>{g.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {g.items.map((label) => {
                    const on = state.interests.includes(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={on}
                        onClick={() => dispatch({ type: 'toggleInterest', label })}
                        style={chip(on)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {state.obStep === 2 && (
          <div style={{ display: 'grid', gap: 10 }}>
            {bundle.days.map((d) => {
              const on = state.obDays.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => dispatch({ type: 'toggleObDay', day: d.key })}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, width: '100%', padding: '14px 15px', borderRadius: 10,
                    cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                    background: on ? 'rgba(var(--color-accent-rgb), 0.14)' : 'var(--color-surface)',
                    color: 'var(--color-text)',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ font: '500 15px/1.2 var(--font-body)' }}>{d.label}</div>
                    <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 3 }}>{d.note}</div>
                  </div>
                  <div
                    style={{
                      flex: 'none', width: 20, height: 20, borderRadius: '50%',
                      border: `1.5px solid ${on ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.25)'}`,
                      background: on ? 'var(--color-accent)' : 'transparent',
                      boxShadow: on ? 'inset 0 0 0 4px var(--color-surface)' : 'none',
                    }}
                  />
                </button>
              );
            })}
            <div style={{ marginTop: 6, padding: 14, borderRadius: 8, background: 'var(--color-accent-900)' }}>
              <div style={{ font: '500 12px/1.4 var(--font-body)', color: 'var(--color-accent-200)' }}>
                {state.interests.length} interests · {state.obDays.length} days selected
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.55, color: t(0.55), marginTop: 4 }}>
                We&rsquo;ll flag matching panels, talent and booths across the app. You can change this any
                time in Me.
              </div>
            </div>
          </div>
        )}
        <div style={{ height: 20 }} />
      </div>

      <div
        style={{
          padding: '14px 26px 42px', display: 'flex', gap: 10, alignItems: 'center',
          background: 'linear-gradient(to top, var(--color-bg) 55%, transparent)',
        }}
      >
        <button
          type="button"
          onClick={() => (state.obStep === 0 ? dispatch({ type: 'obSkip' }) : dispatch({ type: 'obBack' }))}
          style={btn('ghost', { fontSize: 13 })}
        >
          {state.obStep === 0 ? 'Skip' : 'Back'}
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: 'obNext', last })}
          style={btn('primary', { flex: 1, height: 46, fontSize: 15, borderRadius: 10 })}
        >
          {last ? 'Enter the con' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

function Arcs() {
  return (
    <>
      <div
        style={{
          position: 'absolute', top: -160, left: -110, width: 460, height: 460,
          borderRadius: '50%', border: '1px solid rgba(var(--color-accent-rgb), 0.22)', pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute', top: -100, left: -50, width: 340, height: 340,
          borderRadius: '50%', border: '1px solid rgba(var(--color-accent-rgb), 0.14)', pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute', top: -40, left: 10, width: 220, height: 220, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(var(--color-accent-rgb), 0.20), transparent 70%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
